import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { dismissQuoteRequest, getQuoteRequestPage } from '../api/adminApi';
import { logError, toNullableLocaleDateTimeString } from '../utilities';
import ConfirmModal, { ButtonLoadingSpinner, ElementWithKey } from './ConfirmModal';
import GenericDataTable from './GenericDataTable';
import { QuoteRequestResponse } from '../apiModels/quoteRequestResponse';
import { quoteContactMethods } from '../apiModels/quoteContactMethod';
import QuoteRequestDismissButton from './QuoteRequestDismissButton';
import QuoteRequestDetailsModal from './QuoteRequestDetailsModal';

const pageSize = 10;

const TableRowFormatter = (props: { quote: QuoteRequestResponse, setDetailsSelectedQuote: (quoteRequest: QuoteRequestResponse | null) => void, setShowQuoteDetailsModal: (isShown: boolean) => void, setDismissConfirmSelectedQuote: (id: number | null) => void }) => {
    const quote = props.quote;
    const dateTimeCreated = new Date(quote.createdDate);
    const preferredContactMethod = quote.contactMethod === quoteContactMethods.email ? 'Email' : 'Phone';
    const shipmentDetailsPreview = quote.shipmentDetails.length > 20 ? quote.shipmentDetails.substring(0, 20) + '...' : quote.shipmentDetails;

    return (
        <tr key={quote.quoteRequestId}>
            <td>
                <button className="btn btn-sm btn-outline-primary" onClick={() => {
                    props.setDetailsSelectedQuote(quote);
                    props.setShowQuoteDetailsModal(true);
                }}>
                    <i className="bi bi-search"></i>
                </button>
            </td>
            <td>{quote.firstName}</td>
            <td>{quote.lastName}</td>
            <td>{preferredContactMethod}</td>
            <td>{shipmentDetailsPreview}</td>
            <td>{dateTimeCreated.toLocaleDateString()} {dateTimeCreated.toLocaleTimeString()}</td>
            <td className='text-end'>
                <QuoteRequestDismissButton
                    onClick={() => props.setDismissConfirmSelectedQuote(quote.quoteRequestId)} />
            </td>
            <td></td>
        </tr>
    );
};

export default function QuotesListTable() {
    const [ isDataTableLoading, setIsDataTableLoading ] = useState<boolean>(false);
    const [ quoteRequests, setQuoteRequests ] = useState<QuoteRequestResponse[]>([]);
    const [ selectedPage, setSelectedPage ] = useState<number>(0);
    const notifyUserOfError = (err: string) => toast(err, { type: 'error' });
    const notifyUserOfSuccess = (msg: string) => toast(msg, { type: 'success' });

    const [dismissConfirmSelectedQuote, setDismissConfirmSelectedQuote] = useState<number | null>(null);
    const [detailsSelectedQuote, setDetailsSelectedQuote] = useState<QuoteRequestResponse | null>(null);
    const [dismissConfirmProcessing, setDismissConfirmProcessing] = useState<boolean>(false);
    const [showQuoteDetailsModal, setShowQuoteDetailsModal] = useState<boolean>(false);

    const loadQuoteRequests = async() => {
        setIsDataTableLoading(true);

        try {
            let quotes = await getQuoteRequestPage(selectedPage, pageSize);
            setQuoteRequests(quotes);
            setIsDataTableLoading(false);
        } 
        catch (error: any) {
            if (error instanceof Error) {
                notifyUserOfError(error.message);
            }
    
            logError(error);
            setIsDataTableLoading(false);
        }
    };
    
    const dismissRequest = async() => {
        if (dismissConfirmSelectedQuote === null) {
            notifyUserOfError('No quote selected for dismissal.');
        }
        else {
            setDismissConfirmProcessing(true);
    
            try{
                await dismissQuoteRequest(dismissConfirmSelectedQuote);
                setDismissConfirmSelectedQuote(null);
                notifyUserOfSuccess('Quote request dismissed succesfully.');
                await loadQuoteRequests();
            } 
            catch (error: any) {
                if (error instanceof Error) {
                    notifyUserOfError(error.message);
                }
        
                logError(error);
            }
            finally{
                setDismissConfirmProcessing(false);
            }
        }
    };
    
    useEffect(() => {
        loadQuoteRequests();
    }, [ selectedPage ]);

    const DismissConfirmModalButtons: ElementWithKey[] = [
        { 
            key: 0, 
            element: <button 
                className='btn btn-primary' 
                onClick={() => dismissRequest()}
                disabled={dismissConfirmProcessing}>
                {dismissConfirmProcessing ? <ButtonLoadingSpinner /> : 'Dismiss'}
            </button>
        },
        {
            key: 1,
            element: <button 
                className='btn btn-default' 
                onClick={() => setDismissConfirmSelectedQuote(null)}>
                Cancel
            </button>
        }
    ];

    return (
        <>
            <div className='row'>
                <h3 className='col-9'>Quote Requests</h3>
                
                <div className='col-2 text-end'>
                    {(quoteRequests.length < pageSize && selectedPage === 0) || 
                        <div className='col-12 text-center'>
                            <button className='btn btn-default' hidden={selectedPage === 0} onClick={() => setSelectedPage(selectedPage - 1)} disabled={selectedPage === 0}>
                                <i className='bi bi-chevron-left fs-4 fw-bold'></i>
                            </button>
                            <span className='mx-2'>{selectedPage + 1}</span>
                            <button className='btn btn-default' hidden={quoteRequests.length < pageSize} onClick={() => setSelectedPage(selectedPage + 1)} disabled={quoteRequests.length < pageSize}>
                                <i className='bi bi-chevron-right fs-4 fw-bold'></i>
                            </button>
                        </div>
                    }
                </div>

                <div className='col-1 text-end'>
                    <button className='btn btn-default' onClick={() => loadQuoteRequests()}>
                        <i className='bi bi-arrow-repeat fs-4 fw-bold'></i>
                    </button>
                </div>
            </div>

            <GenericDataTable data={quoteRequests} 
                dataFormatter={data => <>{data.map(d => <TableRowFormatter key={d.quoteRequestId} quote={d} setDetailsSelectedQuote={setDetailsSelectedQuote} setDismissConfirmSelectedQuote={setDismissConfirmSelectedQuote} setShowQuoteDetailsModal={setShowQuoteDetailsModal} />)}</>}
                noDataMessage='There are no pending quote requests.'
                columnCount={6}
                isLoading={isDataTableLoading}>
                <tr>
                    <td></td>
                    <td>First Name</td>
                    <td>Last Name</td>
                    <td>Preferred Contact Method</td>
                    <td>Details</td>
                    <td>Created</td>
                </tr>
            </GenericDataTable>
            
            <ToastContainer />
            <ConfirmModal title='Confirm Dismissal' 
                body='Are you sure you want to dismiss this quote request? This quote will be moved to the quote history list. This is irreversible.' 
                buttons={DismissConfirmModalButtons} 
                show={dismissConfirmSelectedQuote !== null} 
                setShow={() => setDismissConfirmSelectedQuote(null)} />
            {detailsSelectedQuote != null &&
            <QuoteRequestDetailsModal
                show={showQuoteDetailsModal} 
                setShow={setShowQuoteDetailsModal}
                quoteRequest={detailsSelectedQuote} />
            }
        </>
    );
}
